import { useState } from "react";
import { IconBottle, IconMoon, IconPlus } from "./icons";
import type { EventsApi } from "../lib/useEvents";
import { useT, useAmount } from "../lib/LocaleContext";

type Props = {
  open: boolean;
  insert: EventsApi["insert"];
  /** Resolved quick-log amounts (baby default → last logged value). */
  quickFeedOz: number;
  quickNurseOz: number;
  onClose: () => void;
  /** Opens specific named sub-sheets for advanced edit. */
  onOpenSubsheet?: (kind: "sleep" | "feed" | "diaper" | "pump" | "weight" | "note") => void;
};

export function LogSheet({ open, insert, quickFeedOz, quickNurseOz, onClose, onOpenSubsheet }: Props) {
  const t = useT();
  const amount = useAmount();
  const [busy, setBusy] = useState<string | null>(null);

  if (!open) return null;


  async function commit(label: string, fn: () => Promise<unknown>) {
    setBusy(label);
    try {
      await fn();
      onClose();
    } finally {
      setBusy(null);
    }
  }

  return (
    <div style={styles.backdrop} onClick={onClose}>
      <div style={styles.sheet} onClick={(e) => e.stopPropagation()}>
        <div style={styles.handle} />
        <div style={styles.grid}>
          <Tile
            label={t.logSheet.bottle}
            sub={t.logSheet.bottleSub(amount.fmt(quickFeedOz))}
            accent="feed"
            busy={busy === "feed"}
            onClick={() => commit("feed", () => insert({ type: "feed", amount_oz: quickFeedOz }))}
            onLongPress={() => onOpenSubsheet?.("feed")}
            icon={<IconBottle size={26} />}
          />
          <Tile
            label={t.logSheet.breast}
            sub={t.logSheet.breastSub(amount.fmt(quickNurseOz))}
            accent="feed"
            busy={busy === "nurse"}
            onClick={() => commit("nurse", () => insert({ type: "nurse", amount_oz: quickNurseOz }))}
            icon={<IconBottle size={26} />}
          />
          <Tile
            label={t.logSheet.addNap}
            sub={t.logSheet.addNapSub}
            accent="sleep"
            onClick={() => onOpenSubsheet?.("sleep")}
            icon={<IconMoon size={26} />}
          />
          <Tile
            label={t.logSheet.diaper}
            sub={t.logSheet.diaperSub}
            busy={busy === "diaper-open"}
            onClick={() => onOpenSubsheet?.("diaper") ?? null}
            icon={<DiaperGlyph />}
          />
          <Tile
            label={t.logSheet.weight}
            sub={t.logSheet.weightSub}
            busy={busy === "weight-open"}
            onClick={() => onOpenSubsheet?.("weight") ?? null}
            icon={<WeightGlyph />}
          />
          <Tile
            label={t.logSheet.note}
            sub={t.logSheet.noteSub}
            busy={busy === "note"}
            onClick={() => onOpenSubsheet?.("note") ?? null}
            icon={<IconPlus size={26} />}
          />
        </div>
        <button style={styles.cancel} onClick={onClose}>
          {t.logSheet.cancel}
        </button>
      </div>
    </div>
  );
}

function Tile({
  label,
  sub,
  accent,
  prominent,
  busy,
  onClick,
  onLongPress,
  icon,
}: {
  label: string;
  sub?: string;
  accent?: "feed" | "sleep";
  prominent?: boolean;
  busy?: boolean;
  onClick?: () => void;
  onLongPress?: () => void;
  icon: React.ReactNode;
}) {
  const accentColor = accent === "feed" ? "var(--bh-feed)" : accent === "sleep" ? "var(--bh-sleep)" : "var(--bh-ink-2)";
  const bg = prominent && accent
    ? accent === "feed" ? "var(--bh-feed)" : "var(--bh-sleep)"
    : "var(--bh-surface-2)";
  const ink = prominent && accent
    ? accent === "feed" ? "oklch(0.2 0.04 70)" : "oklch(0.2 0.04 230)"
    : "var(--bh-ink)";
  const iconColor = prominent ? ink : accentColor;
  const labelColor = prominent ? ink : "var(--bh-ink)";
  const subColor = prominent ? ink : "var(--bh-ink-3)";

  // Long-press: 500ms hold → onLongPress.
  let pressTimer: ReturnType<typeof setTimeout> | null = null;
  const start = () => {
    if (!onLongPress) return;
    pressTimer = setTimeout(() => {
      pressTimer = null;
      onLongPress();
    }, 500);
  };
  const end = () => {
    if (pressTimer) {
      clearTimeout(pressTimer);
      pressTimer = null;
    }
  };

  return (
    <button
      style={{
        ...styles.tile,
        background: bg,
        color: ink,
        opacity: busy ? 0.6 : 1,
      }}
      disabled={busy}
      onClick={onClick}
      onPointerDown={start}
      onPointerUp={end}
      onPointerCancel={end}
      onPointerLeave={end}
    >
      <div style={{ ...styles.tileIcon, color: iconColor }}>{icon}</div>
      <div style={{ ...styles.tileLabel, color: labelColor }}>{label}</div>
      {sub && <div style={{ ...styles.tileSub, color: subColor }}>{sub}</div>}
    </button>
  );
}

function DiaperGlyph() {
  return (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 7c0-1 1-2 2-2h10c1 0 2 1 2 2v4c0 4-3 8-7 8s-7-4-7-8V7Z" />
      <path d="M9 12h6" opacity=".55" />
    </svg>
  );
}

function WeightGlyph() {
  return (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 9h16l-1.5 10a2 2 0 0 1-2 1.7H7.5a2 2 0 0 1-2-1.7L4 9Z" />
      <path d="M9.5 9a2.5 2.5 0 1 1 5 0" />
    </svg>
  );
}

const styles: Record<string, React.CSSProperties> = {
  backdrop: {
    position: "fixed",
    inset: 0,
    background: "oklch(0 0 0 / 0.55)",
    zIndex: 100,
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "center",
    animation: "bh-fade 0.15s ease",
  },
  sheet: {
    background: "var(--bh-surface)",
    width: "100%",
    maxWidth: 520,
    borderTopLeftRadius: 28,
    borderTopRightRadius: 28,
    padding: "10px 16px max(28px, env(safe-area-inset-bottom))",
    boxShadow: "0 -20px 60px oklch(0 0 0 / 0.4)",
    animation: "bh-slide-up 0.2s ease",
    maxHeight: "88vh",
    overflowY: "auto",
  },
  handle: {
    width: 40,
    height: 4,
    background: "var(--bh-divider)",
    borderRadius: 2,
    margin: "0 auto 14px",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 12,
    marginBottom: 16,
  },
  tile: {
    appearance: "none",
    border: "none",
    minHeight: 110,
    borderRadius: 22,
    padding: "16px 14px",
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 6,
    cursor: "pointer",
    fontFamily: "inherit",
    textAlign: "left",
  },
  tileIcon: { display: "inline-flex" },
  tileLabel: { fontSize: 17, fontWeight: 600 },
  tileSub: { fontSize: 13 },
  cancel: {
    appearance: "none",
    width: "100%",
    border: "none",
    background: "var(--bh-surface-2)",
    color: "var(--bh-ink-2)",
    padding: "14px",
    borderRadius: 16,
    fontSize: 16,
    fontWeight: 500,
    cursor: "pointer",
    minHeight: 56,
  },
};
